import { useNavigate } from 'react-router-dom';
import Hero from '../components/Hero';

const Accreditation = () => {
  const navigate = useNavigate();

  const evidenceCriteria = [
    { num: 1, title: 'Scientific Grounding', description: 'Are techniques referenced to their evidence tier with appropriate sources?' },
    { num: 2, title: 'Accuracy of Claims', description: 'Does the curriculum use claim language appropriate to each technique’s evidence tier?' },
    { num: 3, title: 'Contraindication Awareness', description: 'Are coaches taught when NOT to use specific techniques?' },
    { num: 4, title: 'Ethics & Scope Framing', description: 'Is the coaching-therapy distinction taught with referral protocols?' },
    { num: 5, title: 'Evidence Literacy', description: 'Are coaches taught to evaluate and communicate evidence, not just receive it?' },
    { num: 6, title: 'Client Informed Consent', description: 'Are coaches trained to disclose the evidence tier of their techniques to clients?' }
  ];

  const credentialLevels = [
    {
      level: 'AAC',
      title: 'Associate Accredited Coach',
      requirements: ['60 training hours', '10 coaching hours', 'Evidence Literacy Orientation', 'Ethics Foundation module'],
      description: 'Entry-level recognition for coaches who have completed foundational training in any consciousness-based coaching modality.'
    },
    {
      level: 'PAC',
      title: 'Professional Accredited Coach',
      requirements: ['125 training hours', '500 coaching hours', '50 supervision hours', 'Active AAC credential'],
      description: 'The primary professional credential for established coaches with a developed practice and demonstrated competence.'
    },
    {
      level: 'MAC',
      title: 'Master Accredited Coach',
      requirements: ['200 training hours', '2,500 coaching hours', 'Panel interview required'],
      description: 'ICCAC’s highest individual credential for coaches with exceptional depth, experience, and documented professional contribution.'
    },
    {
      level: 'ACT',
      title: 'Accredited Coach Trainer',
      requirements: ['Active MAC', '5 years training delivery', '5,000 coaching hours', 'Training facilitation assessment'],
      description: 'For coaches who professionally train other coaches in consciousness-based methodologies.'
    },
    {
      level: 'ACS',
      title: 'Accredited Coaching School',
      requirements: ['Passes full 10-criterion Evidence Audit', 'Faculty qualification review', 'Student protection governance'],
      description: 'Institutional accreditation for training organizations whose curriculum, faculty, and governance meet ICCAC’s comprehensive quality standards.'
    }
  ];

  const process = [
    { step: 1, title: 'Submit Application', description: 'Submit your documentation through the ICCAC portal.' },
    { step: 2, title: 'Evidence Audit', description: 'Full 10-criterion audit of your curriculum by an independent review panel.' },
    { step: 3, title: 'Competency Review', description: 'Sample student work assessed against the ICCAC Consciousness Coaching Competency Framework.' },
    { step: 4, title: 'Assessment Report', description: 'Written report with scores, strengths, development areas, and recommendation within 30 days.' },
    { step: 5, title: 'Decision & Credential', description: 'Approved applicants receive credential, digital badge, and Global Coach Directory listing.' }
  ];

  return (
    <div>
      <Hero
        title="Get Your Coaching Program Accredited"
        subtitle="When your program carries the ICCAC seal, clients know they're working with a coach assessed honestly and trained in evidence literacy"
        ctaText="Start Your Application →"
        onCtaClick={() => navigate('/contact')}
      />

      {/* Differentiator */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-6">Our Differentiator</h2>
            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
              Every Program Undergoes a 10-Criterion Evidence Audit
            </h3>
            <p className="text-lg text-gray-600">
              We assess not just what you teach, but how honestly you represent the evidence for what you teach. 
              This is what no other accreditation body does, and it is the core of what an ICCAC credential means.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {evidenceCriteria.map((criterion) => (
              <div key={criterion.num} className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold mr-4">
                    {criterion.num}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">{criterion.title}</h4>
                    <p className="text-gray-600">{criterion.description}</p>
                  </div>
                </div>
              </div>
            ))}
            <div className="bg-blue-50 p-6 rounded-lg border-2 border-[#1e3a5f]">
              <div className="flex items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold mr-4">
                  +4
                </div>
                <div>
                  <h4 className="text-lg font-bold text-[#1e3a5f] mb-2">Four Further Criteria</h4>
                  <p className="text-gray-600">
                    Technique transparency, restricted claims education, outcome measurement, and continuing education framework.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Credential Levels */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4 text-center">ICCAC Accreditation Levels</h2>
          <p className="text-xl text-gray-600 text-center mb-12">Choose the credential that matches your experience</p>
          
          <div className="space-y-6">
            {credentialLevels.map((credential) => (
              <div key={credential.level} className="bg-white p-8 rounded-lg shadow-md">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-4">
                      <span className="text-2xl font-bold text-[#1e3a5f] bg-blue-100 px-4 py-2 rounded mr-4">
                        {credential.level}
                      </span>
                      <h3 className="text-2xl font-bold text-gray-800">{credential.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-4">{credential.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {credential.requirements.map((req, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                          {req}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The Process */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#1e3a5f] mb-4">How Accreditation Works</h2>
            <p className="text-xl text-gray-600">
              From application to accreditation in 30 working days for individuals, 45 days for institutions
            </p>
          </div>

          <div className="relative">
            {process.map((item, index) => (
              <div key={item.step} className="flex items-start mb-8">
                <div className="flex-shrink-0 w-12 h-12 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center font-bold text-xl mr-6">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-[#1e3a5f] mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <button
              onClick={() => navigate('/contact')}
              className="bg-[#1e3a5f] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#2d5380] transition-colors text-lg"
            >
              Begin Your Application →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accreditation;